import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closePersonPhotoDialog } from 'reducers/PersonDialogsReducer';
import Dialog from 'components/RMSDialog';
import TextField from '@material-ui/core/TextField';
import { uploadPersonPhoto } from 'reducers/helpers/fileHelpers';
import { notify } from 'reducers/NotifierReducer';
import { handleError } from 'reducers/ErrorReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import Tooltip from 'components/Tooltip';
import GetAppIcon from '@material-ui/icons/GetApp';
import AttachmentIcon from 'components/AttachmentIcon';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import formStyles, {
  gridStyle,
  Row,
  RowInner,
  ColCard,
  Fills,
  formatSaveData,
} from 'utils/formStyles';
import Dictionary from 'components/RMSAutoComplete/RMSDictonary';
import RMSCheckbox from 'components/RMSCheckbox';
import { getDefaultMugshotType } from 'reducers/AgencySettingsReducer';

const useStyles = makeStyles((theme) => ({
  txtField: {
    minWidth: 600,
  },
  fileInput: {
    display: 'none',
  },
  dropZone: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  dropZoneOver: {
    background: theme.colors.grey1,
    height: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `3px dashed ${theme.palette.primary.main}`,
    marginBottom: 8,
    '& svg': {
      marginRight: 8,
    },
  },
  fileInfo: {
    minHeight: 35,
    '& img': {
      maxHeight: 24,
      borderRadius: 3,
      marginRight: 8,
    },
  },
  item: gridStyle(200, 800),
}));

function AddPersonPhotoAttachment(props) {
  const classes = useStyles();
  const { ptsPersonID, IsMarkType, rows } = props;
  const [Description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [draggedOver, setDraggedOver] = useState(false);
  const [ImageTypeCode, setImageTypeCode] = useState(null);
  const [MarkType, setMarkType] = useState(null);
  const [Location, setLocation] = useState(null);
  const [Gang, setGang] = useState(null);
  const [IsMark, setIsMark] = useState(IsMarkType ? true : false);
  const [IsDefault, setIsDefault] = useState(false);
  const defaultMugshotType = getDefaultMugshotType();
  useEffect(() => {
    if (defaultMugshotType) {
      if (ImageTypeCode && ImageTypeCode?.Code === defaultMugshotType) {
        setIsDefault(true);
      } else {
        setIsDefault(false);
      }
    }
  }, [ImageTypeCode]);
  const close = () => {
    props.closePersonPhotoDialog();
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files[0];
    setFile(file);
  };

  const save = async () => {
    const data = formatSaveData({
      ImageTypeCode,
      IsMark,
      Location,
      Gang,
      Description,
      MarkType,
      IsDefault,
    });
    data.ptsPersonID = ptsPersonID;
    data.file = file;
    props.showSpinner();
    try {
      await uploadPersonPhoto(data);
      props.notifyDataUpdate({ type: 'person-image-upload', data: {} });
      props.notify('File uploaded', 'success');
    } catch (err) {
      props.handleError(err);
    }
    props.hideSpinner();
    close();
  };

  const handleDefaultValue = (value) => {
    const hasDefault = rows.find((row) => row.IsDefault);
    if (value && hasDefault)
      if (
        !window.confirm(
          'This record already has a default photo. Would you like to make this photo the default instead?'
        )
      )
        return;

    setIsDefault(!IsDefault);
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" variant="contained" autoFocus onClick={save} disabled={!file}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const onDrop = (ev) => {
    const files = ev.dataTransfer.files;
    if (!files.length) return setDraggedOver(false);
    setFile(files[0]);
    ev.preventDefault();
  };

  const onDragOver = (ev) => {
    if (!draggedOver) setDraggedOver(true);
    ev.preventDefault();
  };

  const onDragLeave = (ev) => {
    if (draggedOver) setDraggedOver(false);
    ev.preventDefault();
  };

  const renderFileInfo = () => {
    const idx = file.name.lastIndexOf('.');
    const type = file.name.substr(idx);
    return (
      <>
        <AttachmentIcon type={type} />
        <span>{file.name}</span>
      </>
    );
  };

  const renderInfoForm = () => {
    return (
      <div>
        <Row>
          <RowInner>
            <Dictionary
              options="codeMugshotTypes"
              className={classes.item}
              onChange={(ev, val) => setImageTypeCode(val)}
              value={ImageTypeCode}
              formType="add"
              label="Image Type"
              IsMark={IsMarkType ? true : false}
              compact
            />
          </RowInner>
          <RowInner>
            <TextField
              label="Description"
              variant="outlined"
              size="small"
              value={Description}
              onChange={(e) => setDescription(e.target.value)}
              className={classes.item}
            />
          </RowInner>
        </Row>
        {!IsMarkType && (
          <>
            <RMSCheckbox
              selectedCheckbox={IsMark}
              handleFormChange={(e) => {
                setIsMark(e.target.checked);
                if (e.target.checked) {
                  setIsDefault(false);
                }
              }}
              label="Scar, Mark, or Tattoo"
            />
            <RMSCheckbox
              selectedCheckbox={IsDefault}
              handleFormChange={(e) => handleDefaultValue(e.target.checked)}
              label="Is Default"
              disabled={IsMark}
            />
          </>
        )}

        {IsMark && (
          <Row>
            <ColCard minWidth={500}>
              <RowInner>
                <Dictionary
                  options="codeMarks"
                  className={classes.item}
                  onChange={(ev, val) => setMarkType(val)}
                  value={MarkType}
                  formType="add"
                  label="Mark Type"
                  compact
                />
                <Dictionary
                  options="codeMarkLocation"
                  className={classes.item}
                  onChange={(ev, val) => setLocation(val)}
                  value={Location}
                  formType="add"
                  label="Location"
                  compact
                />
              </RowInner>
              <RowInner>
                <Dictionary
                  options="codeGang"
                  className={classes.item}
                  onChange={(ev, val) => setGang(val)}
                  value={Gang}
                  formType="add"
                  label="Gang"
                  compact
                />
              </RowInner>
            </ColCard>
          </Row>
        )}
      </div>
    );
  };

  const inputId = `upload-file`;
  const className = draggedOver || Boolean(file) ? classes.dropZoneOver : classes.dropZone;
  return (
    <Dialog toolbar onClose={close} title="Add Photo" actions={renderActions()}>
      <div className={className} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
        <Tooltip title="Add Attachment">
          <label htmlFor={inputId}>
            <Button color="primary" variant="outlined" component="span">
              <GetAppIcon /> Add Attachment
            </Button>
          </label>
        </Tooltip>
        <input
          accept="image/*"
          className={classes.fileInput}
          onChange={handleAttachmentChange}
          id={inputId}
          type="file"
        />
      </div>
      <div className={classes.fileInfo}>{Boolean(file) && renderFileInfo()}</div>
      {renderInfoForm()}
    </Dialog>
  );
}

export default connect(null, {
  closePersonPhotoDialog,
  notify,
  handleError,
  showSpinner,
  hideSpinner,
  notifyDataUpdate,
})(AddPersonPhotoAttachment);
